import React from 'react'
import styled from 'styled-components'
import { IconType } from 'react-icons'
import { Color } from '../../interfaces/designSystem'
import { Button, ButtonProps } from 'antd'
import Flex from './Flex'
import { useRouter } from 'next/navigation'
import Box, { BoxProps } from '@ui/Box'

const Btn = styled(Button as any)<ButtonProps>`
  ${({ type }) => type === 'link' && `padding: 0;`};
  ${({ color }) => color && `color: ${color};`};
`

const LoadingSpan = styled.span`
  animation: loadingCircle 1s infinite linear;

  @keyframes loadingCircle {
    100% {
      transform: rotate(360deg);
    }
  }
`

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'color'> {
  icon?: IconType
  iconPostfix?: IconType
  iconSize?: number
  children?: React.ReactNode
  color?: Color
  to?: string
  center?: boolean
  simple?: boolean
  containerProps?: BoxProps
}

const IconButton = ({
  icon: Icon,
  iconPostfix: IconPostfix,
  onClick,
  iconSize = 22,
  children,
  color,
  to,
  center = false,
  type = 'link',
  simple,
  loading,
  block,
  disabled,
  containerProps,
  ...props
}: IconButtonProps) => {
  const router = useRouter()
  const btnBlocked = loading || disabled

  const loadingStyle = {
    opacity: 0.65,
    cursor: 'not-allowed',
  }

  const onHandleClick = e => {
    if (btnBlocked) {
      e.stopPropagation()
      e.preventDefault()
      return
    }

    if (to) {
      router.push(to)
      return
    }

    onClick?.(e)
  }

  return (
    <Box {...containerProps}>
      <Btn
        onClick={onHandleClick}
        type={type}
        color={!children && !color ? Color.DarkGrey : color}
        loading={false}
        style={btnBlocked ? (loadingStyle as object) : undefined}
        block={block}
        {...props}
      >
        <Flex alignItems="center" justifyContent={center || block ? 'center' : 'start'}>
          {loading ? (
            <>
              <LoadingSpan role="img" aria-label="loading">
                <svg
                  viewBox="0 0 1024 1024"
                  focusable="false"
                  data-icon="loading"
                  width="1em"
                  height="1em"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z" />
                </svg>
              </LoadingSpan>
              {children && <>&nbsp;&nbsp;{children}</>}
            </>
          ) : (
            <>
              {!!Icon && (
                <>
                  <Icon size={iconSize} />
                  {children && <>&nbsp;</>}
                </>
              )}
              {children}
              {IconPostfix && (
                <>
                  &nbsp; <IconPostfix size={iconSize} />
                </>
              )}
            </>
          )}
        </Flex>
      </Btn>
    </Box>
  )
}

export default IconButton
